import Headings from "../../components/Headings/Headings";
import VerticalTabs from "../../components/Tab/VerticalTab";
import { AdminRoles } from "../../utils/constants/app-constants";
import { useEffect, useState } from "react";

function FaqComponent() {
  const userRole = localStorage.getItem("role") ?? "";
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const tabsData = [
    {
      id: 1,
      label: "Getting Started",
      faqs :[
        {
          id: 1,
          question: "What is the Basic Emergency Care course?",
          answer: (
            <>
              The World Health Organization (WHO), in collaboration with the International
              Committee of the Red Cross (ICRC) and the International Federation for
              Emergency Medicine (IFEM), developed the{" "}
              <a
                href="https://www.who.int/teams/integrated-health-services/clinical-services-and-systems/emergency-and-critical-care/bec"
                target="_blank"
                rel="noopener noreferrer"
              >
                Basic Emergency Care (BEC) course
              </a>{" "}
              for first contact providers who manage acute life-threatening conditions
              with limited resources. The central aim of the BEC is to enhance the capacity
              of first contact health workers to provide quality care using a standardized
              approach to the recognition and management of emergency conditions.
            </>
          )
        },        
        {
          id: 2,
          question: "Who certifies the BEC?",
          answer: (
            <>
              WHO does not currently accredit or certify courses. 
              However, there are multiple other pathways by which certification may occur. 
              For example, a Ministry of Health (MOH) may offer its own courses and provide certification 
              through national or regional professional societies. When such mechanisms are not available, 
              alternate means of certification and quality control are required. 
              The International Federation for Emergency Medicine (IFEM) and the 
              International Committee of the Red Cross (ICRC) are certifying organizations 
              with independent mechanisms for standardization of training and facilitator quality control.
            </>
          )
        },
        {
          id: 3,
          question: "What is the BEC Training Database?",
          answer: (
            <>
              The BEC training database is a centralized database designed to consolidate information about BEC courses worldwide. 
              It allows certifying organizations, sponsoring organizations, and course directors to record essential BEC facilitator 
              and participant information, course details, and training outcomes in a standardized and secure way.
            </>
          )
        },
        {
          id: 4,
          question: "Who can access the BEC Training Database??",
          answer: (
            <>
              Access to the BEC training database is limited to authorized users such as sponsoring organization focal points, 
              course coordinators, and other personnel approved by each certifying and/or sponsoring organization.
            </>
          )
        },
        {
          id: 5,
          question: "What information is stored in the BEC Training Database?",
          answer: (
            <>
              <p>
                The BEC training database hosts key information about each registered
                course and its participants to ensure accurate monitoring and reporting
                in a secure way. The main data fields include:
              </p>
        
              <ol>
                <li>
                  <strong>Course Details:</strong> course type (Classic/ Hybrid), dates
                  and duration, region, country, and city of implementation.
                </li>
                <li>
                  <strong>Facilitators:</strong> name, email, role, and cadre.
                </li>
                <li>
                  <strong>Participants:</strong> name, email, primary workplace, cadre,
                  and completion status.
                </li>
                <li>
                  <strong>Documents:</strong> supporting files such as course concept
                  note, and the complete coordinator workbook.
                </li>
              </ol>
        
              <p>
                It does not collect any personal health or sensitive information. All
                data are stored securely and used solely for monitoring, reporting, and
                improving the delivery of BEC trainings worldwide.
              </p>
            </>
          )
        },
        {
          id: 6,
          question: "Is my data secure?",
          answer: (
            <>
              Yes. The BEC training database follows strict data protection and privacy standards. 
              Access is restricted and monitored, and data are stored on secure servers.
            </>
          )
        },
        {
          id: 7,
          question: "Do I need special software to access the BEC Training Database?",
          answer: (
            <>
              No. The BEC training database is web-based and can be accessed through any browser (Chrome, Edge, or Safari). 
              A stable internet connection is recommended.
            </>
          )
        }
      ]
    },
    {
      id: 2,
      label: "Navigation",
      faqs: [
        {
          id: 1,
          question: "What do I need to do if I want to deliver a BEC course?",
          answer: (
            <>
              If your organization is planning to conduct a BEC course, please
              review all the course materials in detail. If you do not have access,
              request them from the regional or local certifying organization or by
              contacting{" "}
              <a href="mailto:emergencycare@who.int">
                emergencycare@who.int
              </a>
              . Please also carefully review the BEC Quick Implementation Guide {" "}
              <button type="button" className="link-button">
                &lt;To be provided&gt;
              </button>.
            </>
          )
        },
        {
          id: 2,
          question: "How can I obtain a username and password?",
          answer: (
            <>
              User credentials are created by the BEC Training Database
              administration team. You may request access through your national or
              regional BEC focal point, or via the certifying organization focal
              point in your region or country. Alternatively, you can request access
              by completing the user registration form with your full name,
              organization, and details of the intended BEC training and certifying
              organization.
            </>
          )
        },
        {
          id: 3,
          question: "How do I create a new course?",
          answer: (
            <>
              Once you have an active user account, log in to the BEC Training
              Database and select “New Course”. Enter the required
              details such as course type, dates, location, and facilitators, then
              save the record.
            </>
          )
        },
        {
          id: 4,
          question: "Can multiple facilitators manage the same course?",
          answer: (
            <>
              Yes. You can add co-facilitators to a course so they can view and edit
              participant lists, upload attendance data, and generate certificates.
            </>
          )
        },
        {
          id: 5,
          question: "How do I register participants?",
          answer: (
            <>
             You can enter participant information manually or upload a CSV file using the provided template.
            Please ensure all participant data is accurate before submission.
            </>
          )
        },
        {
          id: 6,
          question: "Can I edit or update a course after submission?",
          answer: (
            <>
              Yes. Authorized users can edit course details or participant information within a limited time after submission. 
              If additional edits are needed later, please contact {" "}
              <button type="button" className="link-button">
                &lt;To be provided&gt;
              </button>.
            </>
          )
        },
        {
          id: 7,
          question: "How do I ask for approval from the certifying organization?",
          answer: (
            <>
             Once your course is entered into the platform, the BEC Training Database automatically notifies the selected certifying organization's focal point to request approval. 
             You will be notified via email when the course is reviewed.
            </>
          )
        }
      ]
    },
    {
      id: 8,
      label: "Technical Support",
      faqs :[
        {
          id: 1,
          question: "What should I do if I forget my password?",
          answer: (
            <>
              You can reset your password directly on the login page by clicking “Forgot password?” and following the instructions. 
              If you encounter issues, contact the {" "}
              <button type="button" className="link-button">
                &lt;To be provided&gt;
              </button> for assitence.
            </>
          )
        },
        {
          id: 2,
          question:
            "I’m having trouble uploading participants or saving my course. What should I do?",
          answer: (
            <>
              Check your internet connection and ensure your data file matches the required format. If the problem continues, contact
              {" "}
              <button type="button" className="link-button">
                &lt;To be provided&gt;
              </button> with a screenshot of
              the error.
            </>
          )
        },
        {
          id: 3,
          question: "Who should I contact for help or general questions?",
          answer: (
            <>
              For questions about national coordination or approvals, reach out to your regional or national BEC focal point(
              {" "}
              <button type="button" className="link-button">
                &lt;To be provided&gt;
              </button>). For any
              additional questions related to the BEC course, you may contact
              {" "}
              <a href="mailto:emergencycare@who.int">
                emergencycare@who.int
              </a>.
            </>
          )
        }
      ]
    }
  ];
  if (loading) {
    return (
      <div className="aui-loader">
        <aui-loader type="content"></aui-loader>
      </div>
    );
  }
  return (
    <div className="container my-4 faq-container">
      {[AdminRoles.SuperAdmin].includes(userRole as AdminRoles) && (
        <aui-breadcrumb
          previouspages='[{"title":"All Organizations","url":"/organization-listing"}]'
          currentpage="Frequently Asked Questions"
          aria-label="Breadcrumb navigation for Super Admin"
          data-testid="superadmin-breadcrumb"
        ></aui-breadcrumb>
      )}
      {[
        AdminRoles.ApprovingAdmin,
        AdminRoles.SponsoringAdmin,
        AdminRoles.MasterTrainer,
      ].includes(userRole as AdminRoles) && (
        <aui-breadcrumb
          previouspages='[{"title":"All Courses","url":"/course-listing"}]'
          currentpage="Frequently Asked Questions"
          aria-label="Breadcrumb navigation for Admin Roles"
          data-testid="admin-breadcrumb"
        ></aui-breadcrumb>
      )}
      <Headings size="h1" className="pb-2 my-3">
        Frequently Asked Questions
      </Headings>
      {/* <div className="searchSection my-4">
        <InputSearch />
      </div> */}
      <div className="vertical-tab-section my-4">
        <VerticalTabs tabsData={tabsData} />
      </div>
    </div>
  );
}

export default FaqComponent;
